import { db } from './schema'

const BACKUP_FORMAT = 'rd-studio-backup-v1'

const TABLE_NAMES = [
  'vault',
  'books',
  'garments',
  'sizeCharts',
  'projects',
  'patterns',
  'branding',
  'settings',
] as const

interface BackupFile {
  format: string
  exportedAt: string
  tables: Record<string, unknown[]>
}

export interface BackupSummary {
  format: string
  exportedAt: string
  counts: Record<string, number>
}

// Serializza i Uint8Array come oggetti { __b64: "..." } per attraversare JSON.
function uint8ToBase64(arr: Uint8Array): string {
  let s = ''
  const CHUNK = 0x8000
  for (let i = 0; i < arr.length; i += CHUNK) {
    s += String.fromCharCode(...arr.subarray(i, i + CHUNK))
  }
  return btoa(s)
}

function base64ToUint8(b64: string): Uint8Array {
  const bin = atob(b64)
  const arr = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i)
  return arr
}

function serializeRow(row: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(row)) {
    if (v instanceof Uint8Array) {
      out[k] = { __b64: uint8ToBase64(v) }
    } else {
      out[k] = v
    }
  }
  return out
}

function deserializeRow(row: unknown): Record<string, unknown> {
  if (!row || typeof row !== 'object') {
    throw new Error('Riga backup non valida')
  }
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(row as Record<string, unknown>)) {
    if (v && typeof v === 'object' && '__b64' in (v as object)) {
      const b = (v as { __b64: unknown }).__b64
      if (typeof b !== 'string') throw new Error(`Campo binario malformato: ${k}`)
      out[k] = base64ToUint8(b)
    } else {
      out[k] = v
    }
  }
  return out
}

export async function exportBackup(): Promise<{ json: string; summary: BackupSummary }> {
  const counts: Record<string, number> = {}
  const tables: Record<string, unknown[]> = {}

  for (const name of TABLE_NAMES) {
    const rows = await db.table(name).toArray()
    tables[name] = rows.map(r => serializeRow(r as Record<string, unknown>))
    counts[name] = rows.length
  }

  const exportedAt = new Date().toISOString()
  const file: BackupFile = {
    format: BACKUP_FORMAT,
    exportedAt,
    tables,
  }

  return {
    json: JSON.stringify(file, null, 2),
    summary: { format: BACKUP_FORMAT, exportedAt, counts },
  }
}

export async function importBackup(json: string): Promise<BackupSummary> {
  let parsed: BackupFile
  try {
    parsed = JSON.parse(json) as BackupFile
  } catch {
    throw new Error('File non è un JSON valido')
  }

  if (!parsed || typeof parsed !== 'object') {
    throw new Error('Formato backup non riconosciuto')
  }
  if (parsed.format !== BACKUP_FORMAT) {
    throw new Error(`Formato backup non supportato: ${parsed.format ?? '?'}`)
  }
  if (!parsed.tables || typeof parsed.tables !== 'object') {
    throw new Error('Backup malformato: manca la sezione "tables"')
  }

  // Pre-validazione: tutte le tabelle attese devono essere presenti come array
  for (const name of TABLE_NAMES) {
    const arr = parsed.tables[name]
    if (!Array.isArray(arr)) {
      throw new Error(`Backup malformato: tabella "${name}" mancante o non valida`)
    }
  }

  const counts: Record<string, number> = {}

  await db.transaction('rw', db.tables, async () => {
    for (const name of TABLE_NAMES) {
      await db.table(name).clear()
    }
    for (const name of TABLE_NAMES) {
      const rows = (parsed.tables[name] as unknown[]).map(deserializeRow)
      if (rows.length > 0) {
        await db.table(name).bulkAdd(rows)
      }
      counts[name] = rows.length
    }
  })

  return {
    format: parsed.format,
    exportedAt: parsed.exportedAt ?? new Date().toISOString(),
    counts,
  }
}

export function summaryLine(summary: BackupSummary): string {
  const parts: string[] = []
  for (const [name, n] of Object.entries(summary.counts)) {
    if (n > 0) parts.push(`${n} ${name}`)
  }
  return parts.join(' · ') || 'vuoto'
}
